import { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import AppMark from './components/AppMark';
import Onboarding from './components/Onboarding';
import GpaTab from './components/GpaTab';
import TargetTab from './components/TargetTab';

const ONBOARDING_KEY = 'uspgpa.onboarded.v1';

const hasOnboarded = () => {
  try {
    return window.localStorage.getItem(ONBOARDING_KEY) === '1';
  } catch {
    return false;
  }
};

function App() {
  const [showOnboarding, setShowOnboarding] = useState(() => !hasOnboarded());
  const [tab, setTab] = useState('gpa');

  const startApp = (initialTab) => {
    try {
      window.localStorage.setItem(ONBOARDING_KEY, '1');
    } catch {
      // private browsing, just skip persistence
    }
    if (initialTab) setTab(initialTab);
    setShowOnboarding(false);
  };

  return (
    <div className="min-h-screen bg-mist text-slate-900 flex flex-col">
      <div
        className={`flex-1 flex flex-col transition-[filter] duration-300 ${
          showOnboarding ? 'blur-md pointer-events-none select-none' : ''
        }`}
        aria-hidden={showOnboarding || undefined}
      >
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-900/10">
          <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppMark size={40} />
              <div>
                <p className="text-[15px] font-bold leading-tight">USP GPA</p>
                <p className="text-xs text-slate-500 leading-tight">for SOLS transcripts</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="How this works"
              onClick={() => setShowOnboarding(true)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div className="w-full max-w-md mx-auto px-4 pt-5 pb-10 flex-1">
          <nav
            className="grid grid-cols-2 gap-1 rounded-full bg-slate-900/[0.06] p-1"
            aria-label="Tools"
          >
            {[
              { id: 'gpa', label: 'GPA' },
              { id: 'target', label: 'Exam target' },
            ].map(item => (
              <button
                key={item.id}
                type="button"
                aria-current={tab === item.id ? 'page' : undefined}
                onClick={() => setTab(item.id)}
                className={`py-2.5 rounded-full text-sm font-semibold transition-colors ${
                  tab === item.id
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <main className="mt-4">{tab === 'gpa' ? <GpaTab /> : <TargetTab />}</main>
        </div>

        <footer className="bg-slate-900 text-white">
          <div className="max-w-md mx-auto px-4 py-5 text-center">
            <p className="text-[15px] font-bold">
              © 2025{' '}
              <a
                href="https://adrianobadiah.online/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold-300 underline underline-offset-4 hover:text-gold-200 transition-colors"
              >
                Adrian Obadiah
              </a>
            </p>
            <p className="mt-1.5 text-xs text-white/50">
              USP GPA · everything runs on your device
            </p>
          </div>
        </footer>
      </div>

      {showOnboarding && <Onboarding onStart={startApp} />}
    </div>
  );
}

export default App;
